/**
 * Injects Facebook / Google Ads / TikTok / LinkedIn pixel snippets after the
 * user accepts marketing-cookies. We rely on usePublicConfig to fetch IDs once
 * per session and only inject what's actually configured.
 *
 * IMPORTANT: This respects GDPR — we only inject if the user has explicitly
 * given consent (localStorage flag set by the cookie banner). Until then,
 * we render nothing and call no tracking.
 */
import { useEffect, useState } from "react";
import { usePublicConfig } from "@/lib/usePublicConfig";

const CONSENT_KEY = "oc_marketing_consent";

function readConsent() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(CONSENT_KEY) === "1";
}

function injectScript(id, src, inner) {
  if (typeof document === "undefined") return;
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  if (src) s.src = src;
  if (inner) s.innerHTML = inner;
  document.head.appendChild(s);
}

export default function PixelInjector() {
  const config = usePublicConfig();
  const tracking = config?.tracking || {};
  const [consent, setConsent] = useState(readConsent());

  // Re-evaluate consent on storage events (cookie banner sets it in a separate tick)
  useEffect(() => {
    const onStorage = () => setConsent(readConsent());
    window.addEventListener("storage", onStorage);
    // Also poll once every 2 seconds for in-tab changes (cookie banner same-tab)
    const t = window.setInterval(onStorage, 2000);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.clearInterval(t);
    };
  }, []);

  // Inject as soon as BOTH consent AND a populated tracking object are available.
  // We re-run on every relevant change; injectScript is idempotent (early-returns
  // when the <script id="..."> already exists).
  useEffect(() => {
    if (!consent) return;
    if (!config) return; // wait until /public-config has resolved

    if (tracking.fb_pixel_id) {
      injectScript(
        "fb-pixel",
        null,
        `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${tracking.fb_pixel_id}');fbq('track','PageView');`
      );
    }
    if (tracking.google_ads_id) {
      injectScript("gtag-loader", `https://www.googletagmanager.com/gtag/js?id=${tracking.google_ads_id}`);
      injectScript(
        "gtag-init",
        null,
        `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${tracking.google_ads_id}');`
      );
    }
    if (tracking.tiktok_pixel_id) {
      injectScript(
        "tiktok-pixel",
        null,
        `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${tracking.tiktok_pixel_id}');ttq.page();}(window,document,'ttq');`
      );
    }
    if (tracking.linkedin_insight_id) {
      injectScript(
        "linkedin-insight",
        null,
        `_linkedin_partner_id = "${tracking.linkedin_insight_id}"; window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || []; window._linkedin_data_partner_ids.push(_linkedin_partner_id); (function(l) { if (!l) { window.lintrk = function(a,b) { window.lintrk.q.push([a,b]) }; window.lintrk.q=[]; } var s = document.getElementsByTagName("script")[0]; var b = document.createElement("script"); b.type = "text/javascript"; b.async = true; b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js"; s.parentNode.insertBefore(b, s); })(window.lintrk);`
      );
    }
  }, [consent, config, tracking.fb_pixel_id, tracking.google_ads_id, tracking.tiktok_pixel_id, tracking.linkedin_insight_id]);

  return null;
}
